/**
 * Conversation store for managing conversation state
 * Uses Zustand for lightweight state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Conversation } from '../api/conversations';

/**
 * Conversation store state interface
 */
export interface ConversationStore {
  // Data state
  conversations: Conversation[];
  selectedConversationId: string | null;
  currentConversationId: string | null;
  lastFetchTime: number | null;

  // UI state
  isLoading: boolean;
  error: string | null;
  isExpanded: boolean; // Navbar expansion state
  searchTerm: string;
  isNavBarLocked: boolean; // Navbar lock state
  isMobileNavbarOpen: boolean;

  // Modal state
  openModalConversationId: string | null;

  // Actions - Data management
  setConversations: (conversations: Conversation[]) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  removeConversation: (id: string) => void;
  setSelectedConversation: (id: string | null) => void;
  setCurrentConversationId: (id: string | null) => void;
  updateConversationDateLastAccessed: (id: string) => void;

  // Actions - UI state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setExpanded: (expanded: boolean) => void;
  toggleExpanded: () => void;
  setNavBarLocked: (locked: boolean) => void;
  setMobileNavbarOpen: (open: boolean) => void;
  toggleMobileNavbar: () => void;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;

  // Actions - Modal state
  openModal: (conversationId: string) => void;
  closeModal: () => void;

  // Actions - Derived state
  getConversationById: (id: string) => Conversation | undefined;
  getFilteredConversations: () => Conversation[];
  getGroupedConversations: () => {
    pinned: Conversation[];
    today: Conversation[];
    yesterday: Conversation[];
    lastWeek: Conversation[];
    older: Conversation[];
  };

  // Actions - Cache management
  markFetched: () => void;
  shouldRefetch: (maxAgeMs?: number) => boolean;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  conversations: [],
  selectedConversationId: null,
  currentConversationId: null,
  lastFetchTime: null,
  isLoading: false,
  error: null,
  isExpanded: true,
  searchTerm: '',
  openModalConversationId: null,
  isNavBarLocked: true,
  isMobileNavbarOpen: false,
};

/**
 * Create conversation store with Zustand
 */
export const useConversationStore = create<ConversationStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Data management actions
        setConversations: (conversations: Conversation[]) => {
          set(
            { conversations, lastFetchTime: Date.now() }
          );
        },

        addConversation: (conversation: Conversation) => {
          set(
            (state) => ({
              conversations: [conversation, ...state.conversations],
            })
          );
        },

        updateConversation: (id: string, updates: Partial<Conversation>) => {
          set(
            (state) => ({
              conversations: state.conversations.map((conv) =>
                conv.conversationID === id ? { ...conv, ...updates } : conv
              ),
            })
          );
        },

        removeConversation: (id: string) => {
          set(
            (state) => ({
              conversations: state.conversations.filter(
                (conv) => conv.conversationID !== id
              ),
              selectedConversationId:
                state.selectedConversationId === id
                  ? null
                  : state.selectedConversationId,
            })
          );
        },

        setSelectedConversation: (id: string | null) => {
          set(
            { selectedConversationId: id }
          );
        },

        setCurrentConversationId: (id: string | null) => {
          set(
            { currentConversationId: id }
          );
        },

        updateConversationDateLastAccessed: (id: string) => {
          set(
            (state) => ({
              conversations: state.conversations.map((conv) =>
                conv.conversationID === id
                  ? { ...conv, date_last_accesed: new Date().toISOString() }
                  : conv
              ),
            })
          );
        },

        // UI state actions
        setLoading: (isLoading: boolean) => {
          set({ isLoading });
        },

        setError: (error: string | null) => {
          set({ error, isLoading: false });
        },

        clearError: () => {
          set({ error: null });
        },

        setExpanded: (isExpanded: boolean) => {
          set({ isExpanded });
        },

        toggleExpanded: () => {
          set(
            (state) => ({ isExpanded: !state.isExpanded })
          );
        },

        setNavBarLocked: (locked: boolean) => {
          set({ isNavBarLocked: locked });
        },

        // Mobile navbar actions
        setMobileNavbarOpen: (open: boolean) => {
          set({ isMobileNavbarOpen: open });
        },

        toggleMobileNavbar: () => {
          set((state) => ({ isMobileNavbarOpen: !state.isMobileNavbarOpen }));
        },

        setSearchTerm: (searchTerm: string) => {
          set({ searchTerm });
        },

        clearSearch: () => {
          set({ searchTerm: '' });
        },

        // Modal state actions
        openModal: (conversationId: string) => {
          set({ openModalConversationId: conversationId });
        },

        closeModal: () => {
          set({ openModalConversationId: null });
        },

        // Derived state actions
        getConversationById: (id: string) => {
          const { conversations } = get();
          return conversations.find((conv) => conv.conversationID === id);
        },

        getFilteredConversations: () => {
          const state = get();
          const { conversations, searchTerm } = state;

          if (!searchTerm.trim()) {
            return conversations;
          }

          const term = searchTerm.toLowerCase();
          return conversations.filter(
            (conv) =>
              conv.conversationTitle.toLowerCase().includes(term) ||
              conv.profile.toLowerCase().includes(term)
          );
        },

        getGroupedConversations: () => {
          const state = get();
          const filteredConversations = state.getFilteredConversations();

          // Group conversations by date
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
          const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

          const groups = {
            pinned: [] as Conversation[], // TODO: Implement pinning logic
            today: [] as Conversation[],
            yesterday: [] as Conversation[],
            lastWeek: [] as Conversation[],
            older: [] as Conversation[],
          };

          // Sort by last accessed date (most recent first)
          const sortedConversations = [...filteredConversations].sort((a, b) => {
            const dateA = new Date(a.date_last_accesed).getTime();
            const dateB = new Date(b.date_last_accesed).getTime();
            return dateB - dateA;
          });

          sortedConversations.forEach((conversation) => {
            const accessDate = new Date(conversation.date_last_accesed);

            if (accessDate >= today) {
              groups.today.push(conversation);
            } else if (accessDate >= yesterday) {
              groups.yesterday.push(conversation);
            } else if (accessDate >= lastWeek) {
              groups.lastWeek.push(conversation);
            } else {
              groups.older.push(conversation);
            }
          });

          return groups;
        },

        // Cache management actions
        markFetched: () => {
          set({ lastFetchTime: Date.now() });
        },

        shouldRefetch: (maxAgeMs: number = 5 * 60 * 1000) => { // 5 minutes default
          const state = get();
          const { lastFetchTime } = state;
          if (!lastFetchTime) return true;
          return Date.now() - lastFetchTime > maxAgeMs;
        },

        reset: () => {
          set(initialState);
        },
      }),
      {
        name: 'conversation-store', // Persist key
        partialize: (state: ConversationStore) => ({
          // Only persist certain fields
          selectedConversationId: state.selectedConversationId,
          isExpanded: state.isExpanded,
          isNavBarLocked: state.isNavBarLocked,
          isMobileNavbarOpen: state.isMobileNavbarOpen,
          // Note: We don't persist openModalConversationId or SearchTerm as they should reset on page reload
        }),
      }
    ),
    {
      name: 'ConversationStore', // DevTools name
    }
  )
);

/**
 * Selector hooks for specific state slices
 * These help prevent unnecessary re-renders
 */
export const useConversations = () =>
  useConversationStore((state) => state.conversations);

export const useSelectedConversationId = () =>
  useConversationStore((state) => state.selectedConversationId);

export const useConversationLoading = () =>
  useConversationStore((state) => state.isLoading);

export const useConversationError = () =>
  useConversationStore((state) => state.error);

export const useNavbarExpanded = () =>
  useConversationStore((state) => state.isExpanded);

export const useConversationSearch = () =>
  useConversationStore((state) => ({
    searchTerm: state.searchTerm,
    setSearchTerm: state.setSearchTerm,
    clearSearch: state.clearSearch,
  }));

// Removed useGroupedConversations - now handled with useMemo in hooks
// to prevent infinite re-render loops

// TODO: Remove the below hooks if they are unused, we have useConversations and useConversationDetail hook files
/**
 * Action hooks for store mutations
 */
export const useConversationActions = () =>
  useConversationStore((state) => ({
    setConversations: state.setConversations,
    addConversation: state.addConversation,
    updateConversation: state.updateConversation,
    removeConversation: state.removeConversation,
    setSelectedConversation: state.setSelectedConversation,
    setLoading: state.setLoading,
    setError: state.setError,
    clearError: state.clearError,
    setExpanded: state.setExpanded,
    toggleExpanded: state.toggleExpanded,
    reset: state.reset,
  }));
